import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, HandHeart, Building2, Heart } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { volunteerOpportunities } from '../../data/mock';
import { useContent } from '../../context/ContentContext';
import api, { formatApiErrorDetail } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const VolunteerSignupDialog = ({ opportunity, open, onOpenChange }) => {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (open && user) {
      setName(`${user.first_name} ${user.last_name}`.trim());
      setEmail(user.email);
    }
  }, [open, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post('/volunteer/signup', {
        name,
        email,
        opportunity: opportunity.title,
        message,
      });
      toast.success(`Thank you for signing up for ${opportunity.title}! We'll be in touch soon.`);
      onOpenChange(false);
      setName('');
      setEmail('');
      setMessage('');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!opportunity) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="volunteer-signup-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-[#3c4a2a]">
            Volunteer: {opportunity.title}
          </DialogTitle>
          <DialogDescription>{opportunity.commitment}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="vol-name" className="text-[#3c4a2a]">Full Name *</Label>
            <Input
              id="vol-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="volunteer-name-input"
              className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
              placeholder="Your name"
            />
          </div>
          <div>
            <Label htmlFor="vol-email" className="text-[#3c4a2a]">Email Address *</Label>
            <Input
              id="vol-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="volunteer-email-input"
              className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
              placeholder="your@email.com"
            />
          </div>
          <div>
            <Label htmlFor="vol-message" className="text-[#3c4a2a]">Anything you'd like us to know?</Label>
            <Textarea
              id="vol-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              data-testid="volunteer-message-input"
              className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
              placeholder="Skills, availability, questions… (optional)"
            />
          </div>
          <Button
            type="submit"
            disabled={isSubmitting}
            data-testid="volunteer-submit-btn"
            className="w-full bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white py-5"
          >
            {isSubmitting ? 'Submitting...' : 'Sign Me Up'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const GetInvolvedPage = () => {
  const { c } = useContent();
  const [signupOpportunity, setSignupOpportunity] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const committees = [1, 2, 3].map((i) => ({
    id: String(i),
    title: c(`involved.committee${i}.title`, volunteerOpportunities[i - 1]?.title || ''),
    commitment: c(`involved.committee${i}.commitment`, '2–5 hours/month'),
    description: c(`involved.committee${i}.description`, volunteerOpportunities[i - 1]?.description || ''),
  }));

  const openSignup = (opportunity) => {
    setSignupOpportunity(opportunity);
    setDialogOpen(true);
  };

  return (
    <main className="pt-52">
      {/* Hero Section */}
      <section className="py-20 bg-[#f1e3eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
              {c('involved.hero.eyebrow', 'Make a Difference')}
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#3c4a2a] mt-3 mb-6">
              {c('involved.hero.title', 'Get Involved')}
            </h1>
            <p className="text-lg text-[#3c4a2a]/80 leading-relaxed">
              {c('involved.hero.text', "Whether you're ready to join, volunteer, donate, or partner with us, there are many ways to support our mission and make a difference in Lincoln County.")}
            </p>
          </div>
        </div>
      </section>

      {/* Ways to Get Involved */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Join Us */}
            <Card className="bg-gradient-to-br from-[#5d6b3c] to-[#3c4a2a] text-white border-0">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mb-6">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-serif text-2xl mb-3">Join Us</h3>
                <p className="text-white/80 mb-6">
                  Become a member and join a community of women committed to 
                  leadership and service.
                </p>
                <Link to="/membership">
                  <Button className="w-full bg-white text-[#5d6b3c] hover:bg-white/90">
                    View Membership
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Volunteer */}
            <Card className="bg-[#f1e3eb] border-0">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-full bg-[#bd8cae]/50 flex items-center justify-center mb-6">
                  <HandHeart className="w-7 h-7 text-[#bd8cae]" />
                </div>
                <h3 className="font-serif text-2xl text-[#3c4a2a] mb-3">Volunteer</h3>
                <p className="text-[#3c4a2a]/70 mb-6">
                  Share your time and talents through our volunteer programs 
                  and community initiatives.
                </p>
                <a href="#volunteer">
                  <Button
                    variant="outline"
                    className="w-full border-[#bd8cae] text-[#bd8cae] hover:bg-[#bd8cae] hover:text-white"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* Donate */}
            <Card className="bg-gradient-to-br from-[#bd8cae] to-[#a3688f] text-white border-0">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mb-6">
                  <Heart className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-serif text-2xl mb-3">Donate</h3>
                <p className="text-white/80 mb-6">
                  Your generous gift supports our programs, scholarships, and 
                  community impact.
                </p>
                <Link to="/donate">
                  <Button className="w-full bg-white text-[#bd8cae] hover:bg-white/90">
                    Give Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Partner */}
            <Card className="bg-[#f1e3eb] border-0">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-full bg-[#9dad84]/50 flex items-center justify-center mb-6">
                  <Building2 className="w-7 h-7 text-[#5d6b3c]" />
                </div>
                <h3 className="font-serif text-2xl text-[#3c4a2a] mb-3">Partner</h3>
                <p className="text-[#3c4a2a]/70 mb-6">
                  Partner with us to expand our reach and multiply our 
                  community impact.
                </p>
                <a href="#partner">
                  <Button
                    variant="outline"
                    className="w-full border-[#5d6b3c] text-[#5d6b3c] hover:bg-[#5d6b3c] hover:text-white"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Volunteer Opportunities Section */}
      <section id="volunteer" className="py-20 bg-[#f1e3eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
              {c('involved.committees.eyebrow', 'Give Your Time')}
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a] mt-3 mb-4">
              {c('involved.committees.heading', 'Volunteer Committees')}
            </h2>
            <p className="text-[#3c4a2a]/70">
              {c('involved.committees.text', "Our committees are where the club's work gets planned and done. Join one that fits your interests and talents.")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {committees.map((opportunity) => (
              <Card
                key={opportunity.id}
                className="bg-white border-0 shadow-md hover:shadow-lg transition-shadow"
              >
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-serif text-xl text-[#3c4a2a]">
                      {opportunity.title}
                    </h3>
                    <span className="text-xs bg-[#bd8cae]/50 text-[#bd8cae] px-3 py-1 rounded-full">
                      {opportunity.commitment}
                    </span>
                  </div>
                  <p className="text-[#3c4a2a]/70 text-sm mb-4">
                    {opportunity.description}
                  </p>
                  <Button
                    variant="link"
                    onClick={() => openSignup(opportunity)}
                    data-testid={`volunteer-signup-btn-${opportunity.id}`}
                    className="text-[#5d6b3c] hover:text-[#3c4a2a] p-0"
                  >
                    Sign Up
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section id="partner" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
                Collaborate With Us
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a] mt-3 mb-6">
                {c('involved.partnership.heading', 'Partnership Opportunities')}
              </h2>
              <div className="space-y-4 text-[#3c4a2a]/80">
                <p>
                  {c('involved.partnership.text', "We welcome partnerships with businesses, nonprofits, government agencies, and community organizations that share our commitment to women's leadership and community advancement.")}
                </p>
                <p>
                  Partnership opportunities include:
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#bd8cae] rounded-full mt-2 mr-3" />
                    Event sponsorships and co-hosting
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#bd8cae] rounded-full mt-2 mr-3" />
                    Program collaboration and funding
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#bd8cae] rounded-full mt-2 mr-3" />
                    In-kind donations and services
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-[#bd8cae] rounded-full mt-2 mr-3" />
                    Employee engagement opportunities
                  </li>
                </ul>
              </div>
              <Link to="/contact" className="inline-block mt-8">
                <Button className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white px-8">
                  Discuss Partnership
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={c('involved.partnership.image', 'https://static.prod-images.emergentagent.com/jobs/fc3829d4-353f-4fc4-b817-cd1ab3ff1600/images/b415a8213242b16177fe40a001c12ff6417e45421a697882f725c2c701d54743.jpeg')}
                  alt="Women collaborating at a community partnership meeting"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#9dad84]/30 rounded-full -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#5d6b3c]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-6">
            {c('involved.cta.heading', 'Ready to Get Started?')}
          </h2>
          <p className="text-white/90 text-lg mb-8">
            {c('involved.cta.text', "No matter how you choose to get involved, you'll be joining a community of women committed to making Lincoln County stronger.")}
          </p>
          <Link to="/contact">
            <Button className="bg-[#bd8cae] hover:bg-[#bd8cae] text-[#3c4a2a] px-8 py-6 text-lg">
              Contact Us Today
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <VolunteerSignupDialog
        opportunity={signupOpportunity}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </main>
  );
};

export default GetInvolvedPage;
